import { useEffect, useMemo, useState } from "react";
import {
  faUsersSlash,
  faUsers,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {
  defaultBorders,
  defaultLayouts,
  defaultPadding,
  golfstatusColors,
  GSButton,
  GSEmptyList,
  GSInfoGroup,
  GSItemList,
  golfstatusSpacing,
  useCollapsable,
} from "golfstatus_react_components";
import { useDispatch, useSelector } from "react-redux";
import { selectTournamentMessagingSettings } from "../../../reducers/messagesSlice";
import { getTournamentPaymentSettings } from "../../../reducers/settingsSlice";
import TournamentMessageRecipientGroupLineItem from "./tournament-message-recipient-group-line-item";
import "./tournament-message-recipient-groups-list.scss";

const TournamentMessageRecipientGroupsList = (props) => {
  const {
    tournament,
    recipients,
    onManage,
    onAdd,
    onRemove,
    onView,
    onSelectRecipients,
    selectionView = true,
    isLocked = false,
    isAddLoading = false,
    showAvailability = false,
    isAvailable = true,
    onAvailabilityChange,
    messageType = "message",
  } = props;
  const dispatch = useDispatch();
  const [, toggleGroups, groupsLabel, , , , groupsHidden] = useCollapsable(
    false,
  );

  const [wasAvailabilityClicked, setWasAvailabilityClicked] = useState(false);

  useEffect(() => {
    if (!isAddLoading) setWasAvailabilityClicked(false);
  }, [isAddLoading]);

  const showAvailabilitySpinner = wasAvailabilityClicked && isAddLoading;

  const changeAvailability = (available) => {
    setWasAvailabilityClicked(true);
    onAvailabilityChange?.(available);
  };

  const messagingSettings = useSelector((state) =>
    selectTournamentMessagingSettings(state, tournament?.id),
  );

  useEffect(() => {
    if (tournament?.id) {
      dispatch(getTournamentPaymentSettings(tournament.id));
    }
  }, [dispatch, tournament?.id]);

  const paymentsActive = tournament?.organization?.paymentsActive !== false;
  const hasWaitList = tournament?.hasWaitList === true;
  const donationsActive = messagingSettings?.donationsActive === true;

  const visibleGroups = useMemo(() => {
    const groups = [];
    groups.push({ groupType: "sponsors" });
    groups.push({ groupType: "players" });
    if (hasWaitList) {
      groups.push({ groupType: "waitlist" });
    }
    if (paymentsActive) {
      groups.push({ groupType: "customers" });
      if (donationsActive) {
        groups.push({ groupType: "donors" });
      }
    }
    return groups;
  }, [paymentsActive, hasWaitList, donationsActive]);

  const getRecipientGroup = (groupType) =>
    recipients?.find((r) => r?.groupType === groupType);

  const availableByType = useMemo(() => {
    const map = {};
    (tournament?.availableRecipientGroups ?? []).forEach((g) => {
      if (g?.groupType) map[g.groupType] = g.hasRecipients;
    });
    return map;
  }, [tournament?.availableRecipientGroups]);

  const groupItems = visibleGroups
    .map((group) => {
      const recipientGroup = getRecipientGroup(group.groupType);
      return {
        ...group,
        includeAll: recipientGroup?.includeAll === true,
        recipientCount: recipientGroup?.selectedCount ?? 0,
        sentCount: recipientGroup?.sentCount,
        recipientGroupId: recipientGroup?.id,
        hasRecipients: availableByType[group.groupType],
      };
    })
    .filter((g) =>
      selectionView ? true : g.includeAll || (g.recipientCount ?? 0) > 0,
    );

  const tournamentName = tournament?.name ?? "Tournament";

  const headerGroups = [
    {
      fit: "stretch",
      sections: [
        {
          gap: "small-gap",
          sectionItems: [
            {
              type: "primary full-width",
              sectionItemStyle: { whiteSpace: "normal" },
              value: tournamentName,
            },
          ],
        },
      ],
    },
    {
      sections: [
        {
          sectionItems: [
            {
              type: "primary no-wrap",
              value: (
                <GSButton
                  title={groupsLabel}
                  type="transparent secondary"
                  onClick={toggleGroups}
                />
              ),
            },
          ],
        },
      ],
    },
  ];

  const getGroupsContent = () => {
    if (!selectionView && tournament?.excluded) {
      return (
        <TournamentMessageRecipientGroupLineItem
          isExcluded={true}
          isLocked={isLocked}
          messageType={messageType}
        />
      );
    }
    if (showAvailability && !isAvailable) {
      return (
        <GSEmptyList
          style={{
            width: "100%",
            whiteSpace: "normal",
            textAlign: "center",
            backgroundColor: golfstatusColors.grey50,
            ...defaultBorders.mediumLargeBorderRadius,
            ...defaultPadding.emptyListPad,
          }}
          title={`Registered Participants Excluded`}
          detail={`Participants of ${tournamentName} will not receive this ${messageType}, even if they're available and included in other recipient groups.`}
          actions={[
            {
              title: showAvailabilitySpinner
                ? "Including Participants"
                : "Include Participants",
              type: "white no-wrap",
              buttonIcon: showAvailabilitySpinner ? faSpinner : faUsers,
              isDisabled: showAvailabilitySpinner,
              onClick: () => changeAvailability(true),
            },
          ]}
        />
      );
    }
    if (!selectionView && groupItems.length === 0) {
      return (
        <TournamentMessageRecipientGroupLineItem
          isEmptyState={true}
          isLocked={isLocked}
          onSelectRecipients={onSelectRecipients}
        />
      );
    }
    return (
      <>
        <GSItemList
          type="vertical medium-gap"
          items={groupItems}
          listItem={(item) => (
            <TournamentMessageRecipientGroupLineItem
              {...item}
              selectionView={selectionView}
              isLocked={isLocked}
              isAddLoading={isAddLoading}
              onManage={() => onManage?.(item)}
              onAdd={() => onAdd?.(item)}
              onRemove={() => onRemove?.(item)}
              onView={onView ? () => onView(item) : undefined}
            />
          )}
        />
        {showAvailability && (
          <div
            style={{
              ...defaultLayouts.hStack.align("center"),
              width: "100%",
              paddingTop: golfstatusSpacing.largePad,
            }}
          >
            <GSButton
              title={
                showAvailabilitySpinner
                  ? "Excluding Participants"
                  : "Exclude Participants"
              }
              type="no-wrap"
              buttonIcon={showAvailabilitySpinner ? faSpinner : faUsersSlash}
              isDisabled={showAvailabilitySpinner}
              onClick={() => changeAvailability(false)}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <tournament-message-recipient-groups-list>
      <GSInfoGroup dataGroups={headerGroups} />
      {!groupsHidden && (
        <div style={{ ...defaultLayouts.vStack, width: "100%" }}>
          {getGroupsContent()}
        </div>
      )}
    </tournament-message-recipient-groups-list>
  );
};

export default TournamentMessageRecipientGroupsList;
